import type { PropsWithChildren } from 'react';

import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeProvider } from '@mui/material/styles';

import { MODE_KEY } from '../theme-mode/theme-mode';
import type { FenrirThemeMode } from '../theme-mode/theme-mode';
import { ThemeModeProvider } from '../theme-mode/theme-mode-provider';
import { useDefaultThemeMode } from '../theme-mode/use-default-theme-mode/use-default-theme-mode';

import { themeBase } from './theme-base';

import type {} from '@mui/lab/themeAugmentation';
import type {} from '@mui/material/themeCssVarsAugmentation';

export interface FenrirThemeMinimalProps {
  /**
   * The default mode when the local storage has no mode yet,
   * can be 'light' | 'dark' | 'system'
   * if 'system' is passed it will default to the user's system preference and save 'dark' or 'light' in localStorage.
   * @default 'light'
   */
  initialMode?: FenrirThemeMode;
}

export const theme = createTheme(themeBase);

/** Wrap components that handle application-crash level errors, therefore cannot be localized `*/
export const FenrirMinimalThemeProvider = ({
  // TODO: Remove to allow default mode to be system
  initialMode = 'light',
  children,
  ...rest
}: PropsWithChildren<FenrirThemeMinimalProps>) => {
  const defaultMode = useDefaultThemeMode(initialMode);

  return (
    <ThemeProvider
      theme={theme}
      defaultMode={defaultMode}
      modeStorageKey={MODE_KEY}
      disableTransitionOnChange
      // optimize the performance and prevent the dark mode flickering when users refresh the page
      noSsr
      {...rest}
    >
      <CssBaseline enableColorScheme />
      <ThemeModeProvider defaultMode={defaultMode}>
        {children}
      </ThemeModeProvider>
    </ThemeProvider>
  );
};
